/**
 * Workspace Switcher Component
 *
 * Allows users to view and switch between workspaces.
 * Shows current workspace with role badge and dropdown for switching.
 */

import { ChevronsUpDown, Check, Building2, Loader2 } from 'lucide-react';
import { useWorkspace, type WorkspaceRole } from '@/client/hooks/use-workspace';
import { cn } from '@/client/lib/utils';
import { Badge } from '@/client/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/client/components/ui/dropdown-menu';
import { useSidebar } from '@/client/components/ui/sidebar';

const ROLE_LABELS: Record<WorkspaceRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
};

const ROLE_COLORS: Record<WorkspaceRole, string> = {
  owner: 'bg-primary/10 text-primary border-primary/20',
  admin: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  member: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  viewer: 'bg-slate-500/10 text-slate-600 border-slate-500/20',
};

interface WorkspaceSwitcherProps {
  className?: string;
}

export function WorkspaceSwitcher({ className }: WorkspaceSwitcherProps) {
  const {
    workspaces,
    activeWorkspace,
    isLoading,
    isSwitching,
    switchWorkspace,
    hasMultipleWorkspaces,
  } = useWorkspace();

  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';

  // Loading state
  if (isLoading) {
    return (
      <div className={cn('flex items-center gap-2.5 px-1', className)}>
        <div className="size-8 rounded-lg bg-muted animate-pulse" />
        {!isCollapsed && (
          <div className="flex-1 space-y-1.5">
            <div className="h-4 w-24 bg-muted rounded animate-pulse" />
            <div className="h-3 w-16 bg-muted rounded animate-pulse" />
          </div>
        )}
      </div>
    );
  }

  // No workspace (shouldn't happen in normal flow)
  if (!activeWorkspace) {
    return null;
  }

  // Single workspace - just show the name without dropdown
  if (!hasMultipleWorkspaces) {
    return (
      <div className={cn('flex items-center gap-2.5 px-1', className)}>
        <div className="relative shrink-0">
          <div className="size-8 rounded-lg bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
            <Building2 className="size-4 text-primary" />
          </div>
        </div>
        {!isCollapsed && (
          <div className="flex-1 min-w-0 overflow-hidden">
            <span className="font-medium text-sm block truncate">{activeWorkspace.name}</span>
            <Badge
              variant="outline"
              className={cn(
                'px-1.5 py-0 text-[10px] font-medium border',
                ROLE_COLORS[activeWorkspace.role]
              )}
            >
              {ROLE_LABELS[activeWorkspace.role]}
            </Badge>
          </div>
        )}
      </div>
    );
  }

  // Multiple workspaces - show dropdown
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'flex items-center gap-2.5 px-1 py-1.5 rounded-lg w-full',
            'hover:bg-sidebar-accent transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-primary/20',
            className
          )}
          disabled={isSwitching}
        >
          <div className="relative shrink-0">
            <div className="size-8 rounded-lg bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
              {isSwitching ? (
                <Loader2 className="size-4 text-primary animate-spin" />
              ) : (
                <Building2 className="size-4 text-primary" />
              )}
            </div>
          </div>
          {!isCollapsed && (
            <>
              <div className="flex-1 min-w-0 overflow-hidden text-left">
                <span className="font-medium text-sm block truncate">{activeWorkspace.name}</span>
                <Badge
                  variant="outline"
                  className={cn(
                    'px-1.5 py-0 text-[10px] font-medium border',
                    ROLE_COLORS[activeWorkspace.role]
                  )}
                >
                  {ROLE_LABELS[activeWorkspace.role]}
                </Badge>
              </div>
              <ChevronsUpDown className="size-4 text-muted-foreground shrink-0" />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
          Switch workspace
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {workspaces.map((workspace) => (
          <DropdownMenuItem
            key={workspace.id}
            onClick={() => switchWorkspace(workspace.id)}
            className="flex items-center gap-3 py-2.5 cursor-pointer"
          >
            <div className="size-8 rounded-lg bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center shrink-0">
              <Building2 className="size-4 text-primary/70" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm truncate">{workspace.name}</span>
                {workspace.id === activeWorkspace.id && (
                  <Check className="size-4 text-primary shrink-0" />
                )}
              </div>
              <Badge
                variant="outline"
                className={cn(
                  'px-1.5 py-0 text-[10px] font-medium border mt-0.5',
                  ROLE_COLORS[workspace.role]
                )}
              >
                {ROLE_LABELS[workspace.role]}
              </Badge>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
